import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { CheckCircle, ExpandLess, ExpandMore, ErrorOutline, HelpOutline, TrendingDown, TrendingUp, WarningAmber } from '@mui/icons-material';
import {
  AUTOMATION_PLATFORM_OPTIONS_WITH_ALL,
  DEFAULT_AUTOMATION_PLATFORM_VALUE,
} from '../config/automationPlatforms';
import {
  AutomationTicketType,
  PerformanceAlertItem,
  PerformanceAlertsResponse,
  PerformanceVerdict,
  fetchPerformanceAlerts,
} from '../services/automationInsightsService';
import {
  AutomationFilterProvider,
  buildTimeFilterRequestParams,
  useAutomationFilters,
} from '../contexts/AutomationFilterContext';
import { AssignmentGroupScopeProvider } from '../contexts/AssignmentGroupScopeContext';
import './automation-insights/automationInsightsStatusColors.css';
import AutomationFilterBar from './automation-insights/AutomationFilterBar';
import PlatformPerformancePanel from './automation-insights/PlatformPerformancePanel';
import PlatformSummaryPanel from './automation-insights/PlatformSummaryPanel';
import VisitTracker from './automation-insights/VisitTracker';
import VisitLeaderboardPanel from './automation-insights/VisitLeaderboardPanel';

const ALL_CARDS_LIMIT = 4;

function toFiniteNumber(value: number | null | undefined): number | null {
  if (value === null || value === undefined || Number.isNaN(value) || !Number.isFinite(value)) return null;
  return value;
}

function formatPct(value: number | null | undefined): string {
  const v = toFiniteNumber(value);
  return v === null ? 'N/A' : `${v.toFixed(1)}%`;
}

function formatHours(value: number | null | undefined): string {
  const v = toFiniteNumber(value);
  return v === null ? 'N/A' : `${v.toFixed(1)}h`;
}

function formatSignedPct(value: number | null | undefined): string {
  const v = toFiniteNumber(value);
  if (v === null) return 'N/A';
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
}

function deltaColor(value: number | null | undefined): 'success' | 'warning' | 'error' | 'default' {
  const v = toFiniteNumber(value);
  if (v === null) return 'default';
  return v < 0 ? 'success' : v > 0 ? 'error' : 'default';
}

function fulfillmentColor(value: number | null | undefined): 'success' | 'warning' | 'error' | 'default' {
  const v = toFiniteNumber(value);
  if (v === null) return 'default';
  return v > 0 ? 'success' : v < 0 ? 'warning' : 'default';
}

interface AlertCard {
  item: PerformanceAlertItem;
  category: PerformanceVerdict;
}

/**
 * Verdict → visual treatment. Rebuilt 2026-09-28 alongside the backend's new
 * unified verdict engine (see automation/src/metrics.py, "v3: Performance
 * Verdict Engine"): every automation now gets exactly ONE verdict combining
 * fulfillment AND MTTR, instead of two independently-triggered alerts — so
 * there's no longer a case where a card says "Improved" while silently
 * saying nothing about a collapsing fulfillment rate.
 */
const VERDICT_STYLE: Record<
  PerformanceVerdict,
  { label: string; color: 'success' | 'error' | 'warning' | 'default'; icon: JSX.Element }
> = {
  Improved:     { label: 'Improved',     color: 'success', icon: <CheckCircle fontSize="small" /> },
  Degraded:     { label: 'Degraded',     color: 'error',   icon: <ErrorOutline fontSize="small" /> },
  Mixed:        { label: 'Mixed',        color: 'warning', icon: <WarningAmber fontSize="small" /> },
  Inconclusive: { label: 'Inconclusive', color: 'default', icon: <HelpOutline fontSize="small" /> },
};

/**
 * Automation Insights
 *
 * First migrated slice of the standalone Automation Performance Dashboard
 * (automationenablementopena-anedashboard), rebuilt in MUI to match this
 * platform's look. This ports the "Performance Alerts" panel end-to-end as
 * the first working vertical slice; the remaining panels (Platform Summary,
 * Platform Performance, Agent Performance) are the next chunks of work —
 * see PLATFORM_MIGRATION_CHECKLIST equivalent tracked in this project's task
 * list, not stubbed out here.
 *
 * Talks to topsreliabilityframework-automationapi, this platform's own
 * dedicated Automation Insights service, authenticated with the same
 * sign-in token every other page on this platform already uses (no second
 * scope, no second app registration — that bridge was retired 2026-08-25).
 */
export default function AutomationInsights() {
  return (
    <AutomationFilterProvider>
      {/* Assignment-group drill-down scope (Stage 4, Platform View) — set by
          Platform Summary's expand/select, read by Platform Performance's
          platform + assignment-group dropdowns below it. See
          AssignmentGroupScopeContext.tsx for why this is a separate context
          from the time-filter one above. */}
      <AssignmentGroupScopeProvider>
        {/* Renders nothing; fires the visit-tracking ping/heartbeat for as long
            as this page is mounted. Fully decoupled from filters/panel state —
            see VisitTracker.tsx for the safety design. */}
        <VisitTracker />
        {/* Collapsed-by-default right-side tab; opens a Drawer with the
            leaderboard on click. Fixed-position, so mounting it here (rather
            than inline in AutomationInsightsContent's JSX) doesn't matter. */}
        <VisitLeaderboardPanel />
        <AutomationInsightsContent />
      </AssignmentGroupScopeProvider>
    </AutomationFilterProvider>
  );
}

function AutomationInsightsContent() {
  const { filters } = useAutomationFilters();

  const [platform, setPlatform] = useState<string>(DEFAULT_AUTOMATION_PLATFORM_VALUE);
  const [ticketType, setTicketType] = useState<AutomationTicketType>('RITM');

  const [summary, setSummary] = useState<PerformanceAlertsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alertsOpen, setAlertsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const timeParams = buildTimeFilterRequestParams(filters);

    fetchPerformanceAlerts({
      platform,
      ticketType,
      timeFilterType: timeParams.timeFilterType,
      selectedPeriods: timeParams.selectedPeriods,
      dateFrom: timeParams.dateFrom,
      dateTo: timeParams.dateTo,
    })
      .then((data) => {
        if (cancelled) return;
        setSummary(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load Automation Insights data.');
        setSummary(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [platform, ticketType, filters]);

  const isAllPlatforms = platform === 'all';

  const cards: AlertCard[] = useMemo(() => {
    if (!summary) return [];

    // Degraded/Mixed sorted worst-fulfillment-drop-first; Improved sorted
    // best-fulfillment-gain-first; Inconclusive left in the engine's own
    // order (there's no "worse" ordering for "we can't tell yet").
    const byWorstFulfillment = (a: PerformanceAlertItem, b: PerformanceAlertItem) =>
      (toFiniteNumber(a.delta_fulfillment) ?? 0) - (toFiniteNumber(b.delta_fulfillment) ?? 0);
    const byBestFulfillment = (a: PerformanceAlertItem, b: PerformanceAlertItem) =>
      (toFiniteNumber(b.delta_fulfillment) ?? 0) - (toFiniteNumber(a.delta_fulfillment) ?? 0);

    const capIfAll = <T,>(items: T[]) => (isAllPlatforms ? items.slice(0, ALL_CARDS_LIMIT) : items);

    const topDegraded     = capIfAll([...summary.degraded].sort(byWorstFulfillment));
    const topMixed        = capIfAll([...summary.mixed].sort(byWorstFulfillment));
    const topInconclusive = capIfAll(summary.inconclusive);
    const topImproved     = capIfAll([...summary.improved].sort(byBestFulfillment));

    return [
      ...topDegraded.map((item) => ({ item, category: 'Degraded' as const })),
      ...topMixed.map((item) => ({ item, category: 'Mixed' as const })),
      ...topInconclusive.map((item) => ({ item, category: 'Inconclusive' as const })),
      ...topImproved.map((item) => ({ item, category: 'Improved' as const })),
    ];
  }, [summary, isAllPlatforms]);

  const activePlatformLabel =
    AUTOMATION_PLATFORM_OPTIONS_WITH_ALL.find((p) => p.value === platform)?.label ?? platform;

  return (
    // automation-insights-page: scopes automationInsightsStatusColors.css to
    // just this page — see that file's header comment for why it's needed.
    <Box className="automation-insights-page" sx={{ p: 3 }}>
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Automation Insights
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ServiceNow automation performance — migrated from the standalone Automation Dashboard.
        </Typography>
      </Box>

      <AutomationFilterBar />

      <Paper
        variant="outlined"
        sx={{ mt: 3, mb: 1, px: 2, py: 1.5, borderRadius: '6px', bgcolor: '#f2f7fa', borderColor: 'divider' }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Stack
            direction="row" alignItems="center" spacing={1} sx={{ cursor: 'pointer', userSelect: 'none', flex: 1 }}
            onClick={() => setAlertsOpen((o) => !o)}
          >
            <IconButton size="small" sx={{ bgcolor: '#0067b3', color: '#fff', '&:hover': { bgcolor: '#001a35' }, mr: 0.5 }}>
              {alertsOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
            <Typography variant="h6" fontWeight={600} color="text.primary">Automation alerts</Typography>
            <Chip label="Last 7 days · ignores date filter" size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />
          </Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FormControl size="small" sx={{ minWidth: 180 }} onClick={(e) => e.stopPropagation()}>
              <InputLabel id="automation-platform-label">Platform</InputLabel>
              <Select labelId="automation-platform-label" label="Platform" value={platform} onChange={(e: SelectChangeEvent) => setPlatform(e.target.value)}>
                {AUTOMATION_PLATFORM_OPTIONS_WITH_ALL.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup size="small" exclusive value={ticketType}
              onChange={(_e, value: AutomationTicketType | null) => value && setTicketType(value)}
              onClick={(e) => e.stopPropagation()}
            >
              <ToggleButton value="INC">INC</ToggleButton>
              <ToggleButton value="RITM">SR</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      </Paper>

      <Collapse in={alertsOpen}>
      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 4 }).map((_, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card variant="outlined">
                <CardContent>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                  <Skeleton variant="rectangular" height={64} sx={{ my: 1, borderRadius: 1 }} />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : error ? (
        <Alert severity="error">
          <AlertTitle>Unable to load Automation Insights data</AlertTitle>
          {error}
        </Alert>
      ) : !summary ? (
        <Alert severity="warning">No performance alerts available.</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Platform: {activePlatformLabel} · Ticket type: {summary.ticket_type} · Tickets resolved in the last 7 days
            vs pooled {summary.baseline_weeks}-week baseline (also by resolved date)
            {isAllPlatforms ? ` · Showing up to ${ALL_CARDS_LIMIT} per category` : ''}
          </Typography>

          {cards.length === 0 ? (
            <Alert severity="info">No automations have a significant performance signal for this selection.</Alert>
          ) : (
            <Grid container spacing={2}>
              {cards.slice(0, 8).map(({ item, category }) => (
                <Grid item xs={12} sm={6} md={3} key={item.automation_name}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={600}
                          title={item.automation_name}
                          sx={{
                            pr: 1,
                            minWidth: 0,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.automation_name}
                        </Typography>
                        <Chip
                          size="small"
                          icon={VERDICT_STYLE[category].icon}
                          label={VERDICT_STYLE[category].label}
                          color={VERDICT_STYLE[category].color}
                          sx={{ flexShrink: 0 }}
                        />
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Recent MTTR
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {formatHours(item.recent_mttr)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="flex-end">
                          <Chip
                            size="small"
                            variant="outlined"
                            color={deltaColor(item.delta_mttr_pct)}
                            icon={
                              (toFiniteNumber(item.delta_mttr_pct) ?? 0) < 0 ? (
                                <TrendingDown fontSize="small" />
                              ) : (
                                <TrendingUp fontSize="small" />
                              )
                            }
                            label={`MTTR ${formatSignedPct(item.delta_mttr_pct)}`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            color={fulfillmentColor(item.delta_fulfillment)}
                            label={`Fulfillment ${formatSignedPct(item.delta_fulfillment)}`}
                          />
                        </Stack>
                      </Stack>

                      <Grid container spacing={1} mb={1}>
                        <Grid item xs={6}>
                          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Resolved (last 7d)
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {item.recent_volume}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Baseline (avg resolved/wk)
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {item.baseline_avg_weekly_volume} <Typography component="span" variant="caption" color="text.secondary">({item.baseline_volume} total)</Typography>
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Recent fulfillment
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {formatPct(item.recent_fulfillment)}
                              {' '}
                              <Typography component="span" variant="caption" color="text.secondary">
                                ({item.recent_fulfilled_count}/{item.recent_volume})
                              </Typography>
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Baseline fulfillment
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {formatPct(item.baseline_fulfillment)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                        Baseline MTTR: {formatHours(item.baseline_mttr)}
                      </Typography>

                      <Stack spacing={0.5}>
                        {item.reasons.length > 0 ? (
                          item.reasons.map((reason, idx) => (
                            <Typography key={idx} variant="caption" color="text.secondary">
                              {reason}
                            </Typography>
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">No reason captured</Typography>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
      </Collapse>

      <Divider sx={{ my: 4 }} />

      <PlatformSummaryPanel />

      <Divider sx={{ my: 4 }} />

      <PlatformPerformancePanel />
    </Box>
  );
}
